import { h } from '@stencil/core';
import { breakpoints, classNames, defaultTilesLayoutClass, videoIdSelector } from './_constants';
import {
  Behavior,
  DropdownType,
  FormValue,
  Position,
  SectionBehavior,
  VerticalPosition,
  Viewport,
  VIEWPORTS,
} from './_types';

export function getPageWidth(): number {
    return Math.max(
        document.body.offsetWidth,
        document.documentElement.offsetWidth,
        document.documentElement.clientWidth
    );
}

export function isSmallestViewport(): boolean {
    return (getPageWidth() < breakpoints.s);
}

export function isMobileViewport(): boolean {
    return (getPageWidth() < breakpoints.m);
}

// noinspection JSUnusedGlobalSymbols
export function isTabletViewport(): boolean {
    return (getPageWidth() > breakpoints.s && getPageWidth() < breakpoints.l);
}

export function isSmallerThanDesktopViewport(): boolean {
    return (getPageWidth() < breakpoints.l);
}

export function isSmallerThanViewport(breakpoint: number): boolean {
    return (getPageWidth() < breakpoint);
}

export function isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || isIpad();
}

export function isIpad(): boolean {
    return /Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
}

export function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : {
    r: 255,
    g: 255,
    a: 255
  };
}

export function getBgColor(color, opacity) {
  if (opacity == 0) {
    return 'transparent';
  }

  if (opacity) {
    const rgbColor = hexToRgb(color);
    const bgOpacity = opacity <= 1 ? opacity : opacity / 100;
    return `rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, ${bgOpacity})`;
  }

  return color;
}

export function getSectionBehavior(behavior: string, backgroundColor: string = '#ffffff'): SectionBehavior {
  const sectionBehavior = parseSectionBehavior(behavior);

  const renderMarginDiv = sectionBehavior.renderMarginDiv || false;
  const hexColor = sectionBehavior.backgroundColor || backgroundColor;
  const bgColor = sectionBehavior.backgroundOpacity || sectionBehavior.backgroundOpacity == 0 ? getBgColor(hexColor, sectionBehavior.backgroundOpacity) : hexColor;
  const sticky = sectionBehavior.sticky || false;
  const hideUnderlying = sectionBehavior.hideUnderlying || false;
  const fullVirtualHeight = sectionBehavior.fullVirtualHeight || false;
  const sectionClass = [];
  const hostClass = [];

  // set default section inner padding top | bottom to 0
  if (sectionBehavior.collapsePadding) {
    if (sectionBehavior.collapsePadding.top) {
      sectionClass.push(classNames.collapsePadding.top);
    }
    if (sectionBehavior.collapsePadding.bottom) {
      sectionClass.push(classNames.collapsePadding.bottom);
    }
  }
  // add responsive top | bottom margin to section
  if (sectionBehavior.blockmargin && !renderMarginDiv) {
    if (sectionBehavior.blockmargin.top) {
      sectionClass.push(classNames.blockMargin.top);
    }
    if (sectionBehavior.blockmargin.bottom) {
      sectionClass.push(classNames.blockMargin.bottom);
    }
  }
  // set section sticky class
  if (sticky) {
    sectionClass.push(classNames.sticky.section);
    hostClass.push(classNames.sticky.stencilSticky);
    if (hideUnderlying) {
      hostClass.push(classNames.sticky.hideUnderlying);
    }
  } else {
    hostClass.push(classNames.sticky.stencilNotSticky);
  }
  // set section vh-100 class
  if (fullVirtualHeight) {
    sectionClass.push(classNames.fullVirtualHeight);
  }
  // set hide in viewport host classes
  if (sectionBehavior.hideInViewport) {
    hostClass.push(...renderHiddenViewport(sectionBehavior.hideInViewport));
  }
  // additional classes
  if (sectionBehavior.additionalClasses) {
    sectionClass.push(...sectionBehavior.additionalClasses);
  }

  const sectionStyle = { backgroundColor: bgColor };

  return {
    bgColor: bgColor,
    blockMargin: renderMarginDiv ? sectionBehavior.blockmargin : null,
    fullVirtualHeight: fullVirtualHeight,
    sticky: sticky,
    hostClass: hostClass.join(" "),
    sectionClass: sectionClass.join(" "),
    sectionStyle: sectionStyle
  } as SectionBehavior;
}

export function renderHiddenViewport(conf: Viewport) {
  return VIEWPORTS.filter(vp => conf[vp]).map(vp => {
    if (conf[vp]) {
      return 'hidden-' + vp;
    }
  });
}

export function renderBlockMargin(conf: SectionBehavior, vPos: VerticalPosition) {
  return (
        conf && conf.blockMargin && ((vPos==="top" && conf.blockMargin.top) || (vPos==="bottom" && conf.blockMargin.bottom)) ? <cms-block-margin vPos={vPos} /> : ''
    );
}

export function parseSectionBehavior(conf: string) {
  return (JSON.parse(conf) as Behavior);
}

// update url params
export function updateUrlParams(name: string, value: string = null) {
  const urlParams = new URLSearchParams(window.location.search);
  if (!value || value === '') {
    urlParams.delete(name);
  } else {
    urlParams.set(name, value);
  }
  const params = urlParams.toString() !== '' ? '?' + urlParams : '';
  window.history.replaceState({}, document.title, window.location.origin + window.location.pathname + params);
}

export function getTilesLayoutClass(type: number): string {
  switch (type) {
    default:
      return defaultTilesLayoutClass;
    case 1:
      return 'cols-4';
    case 2:
      return 'cols-2';
  }
}

export function elementIsVisibleInViewport(inner: Element, outer: Element, partiallyVisible: boolean = false): boolean {
  const { top, left, bottom, right } = inner.getBoundingClientRect();
  const rect = outer.getBoundingClientRect() as DOMRect;
  return partiallyVisible
    ? ((top > 0 && top < rect.top) || (bottom > 0 && bottom < rect.bottom)) &&
      ((left > 0 && left < rect.left) || (right > 0 && right < rect.right))
    : top >= rect.top && left >= rect.left && bottom <= rect.bottom && right <= rect.right;
}

export function elementEdgeIsVisible(inner: Element, outer: Element, edge: Position = 'top'): boolean {
  const { top, left, bottom, right } = inner.getBoundingClientRect();
  const rect = outer.getBoundingClientRect() as DOMRect;
  switch (edge) {
    case 'top':
      return (top > 0 && top <= rect.top);
    case 'bottom':
      return (bottom > 0 && bottom <= rect.bottom);
    case 'left':
      return (left > 0 && left <= rect.left);
    case 'right':
      return (right > 0 && right <= rect.right);
  }
}

export function getYoukuUrl(id: string, playerUrl: string, clientId: string, params: string): string {
  let url = playerUrl.replace(videoIdSelector, id);

  if (params) {
    url.concat(url.includes('?') ? '&' : '?').concat(params);
  }

  if (clientId) {
    url.concat('&client_id=' + clientId);
  }

  return url;
}

export function getNavigationHeight():number {
  const mobileNav: HTMLElement = document.querySelector('.navbar');
  if (isMobileViewport() && mobileNav) {
    return mobileNav.offsetHeight;
  }

  const mainNav: HTMLElement = document.querySelector('cms-nav-main')
    ? document.querySelector('cms-nav-main').shadowRoot.querySelector('#headerNavigationApp')
    : document.querySelector('#headerNavigationApp');
  if (mainNav) {
    return  mainNav.offsetHeight;
  }

  return 0;
}

export async function generateHash(encoding: string, input: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest(encoding, data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
}

export function textToBinary(str = '') {
  let res = '';
  res = str.split('').map(char => {
    return char.charCodeAt(0).toString(2);
  }).join(' ');
  return res;
}

export function textToHex(str = '') {
  let result = '';

  for (let i=0; i<str.length; i++) {
    result += str.charCodeAt(i).toString(16);
  }

  return result;
}

export function convertToNumber(input) {
  const number = Number(input);
  if (!isNaN(number)) {
    return number; // Valid number
  }
  return null; // Not a number
}

export function convertToBoolean(input) {
  if (typeof input === "string") {
    input = input.toLowerCase();
    if (input === "true") return true;
    if (input === "false") return false;
  }
  return null; // Not a boolean
}

export function determineType(input) {
  // Check for boolean
  const boolean = convertToBoolean(input);
  if (boolean !== null) return boolean;

  // Check for number
  const number = convertToNumber(input);
  if (number !== null) return number;

  // Default to string
  return input;
}

export function replaceSpecialChars(json: any): any {
  if (typeof json === 'string') {
    // Replace &lsquo; with '
    return json.replace(/&lsquo;/g, "'");
  } else if (Array.isArray(json)) {
    // Process arrays recursively
    return json.map(item => replaceSpecialChars(item));
  } else if (typeof json === 'object' && json !== null) {
    // Process objects recursively
    return Object.fromEntries(
      Object.entries(json).map(([key, value]) => [key, replaceSpecialChars(value)])
    );
  }
  // Return the value unchanged if it's not a string, array, or object
  return json;
}

export interface CmsVector2 {
  x: number,
  y: number
}

export interface CmsPosition {
  top: string,
  right: string,
  bottom: string,
  left: string
}

export interface CmsExtendedFormValue extends FormValue{
  name: string;
  value: string;
  label?: string;
  icon?: string;
  params?: string;
}

export interface CmsMultiselectFormValue extends CmsExtendedFormValue{
  name: string;
  value: string;
  valueList: string[];
  label?: string;
  params?: string;
}

export interface CmsInputDropdownConfig {
  form?: string;
  name: string;
  required: boolean | 'hidden';
  label: string;
  multiselectLabel?: string;
  initialValue?: string;
  initialSelected?: string[];
  placeholder?: string;
  showAll?: string;
  color?: string;
  icon?: string;
  errorMessage?: string;
  setUrlParams?: boolean;
  values: CmsExtendedFormValue[];
  minValue?: string;
  maxValue?: string;
  type?: DropdownType;
  dependency?: string[];
}
